/**
 * Tracks pending ack promises by ackId and resolves/rejects them on ack or timeout.
 */
const pendingAcks: Map<
  string,
  {
    resolve: (value: unknown) => void;
    reject: (reason?: unknown) => void;
    timeout: NodeJS.Timeout;
  }
> = new Map();

/**
 * Waits for an ack with the given ackId, resolves with the message, or rejects on timeout.
 * @param ackId The ack identifier to wait for
 * @returns Promise resolved with the ack message
 */
export function waitForAck(ackId: string): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      pendingAcks.delete(ackId);
      reject(new Error(`Ack ${ackId} timed out after 10s`));
    }, 10000);
    pendingAcks.set(ackId, { resolve, reject, timeout });
  });
}

/**
 * Call this when an ack message arrives. Resolves the pending promise if found.
 * @param ackId The ack identifier
 * @param message The ack message to resolve with
 */
export function handleAck(ackId: string, message?: unknown, error?: unknown): void {
  const entry = pendingAcks.get(ackId);
  if (entry) {
    clearTimeout(entry.timeout);
    if (message !== undefined) {
      entry.resolve(message);
    } else if (error !== undefined) {
      entry.reject(error);
    } else {
      entry.reject(new Error(`Ack ${ackId} received with no message or error`));
    }
    pendingAcks.delete(ackId);
  }
}
